import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    type SliderValue,
    NumericInput,
    ToggleGroup,
    Breadcrumb,
    LineChart,
    SelectBox,
    Separator,
    Avatar,
    Button,
    Slider,
    Alert,
    Badge,
    Chart,
    Card,
    Chip,
    Icon,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    MessageSquare,
    TrendingDown,
    ChevronRight,
    ShoppingCart,
    ChevronLeft,
    BadgeCheck,
    Hourglass,
    Building2,
    MapPin,
    Share2,
    Wallet,
    Waves,
    Phone,
    Heart,
    Ruler,
    Flame,
    Ship,
    Star,
    Bed
} from "lucide-react-native";
import Svg, {
    Ellipse,
    Circle,
    Path,
    Rect,
    Line,
    G
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const HeartIcon = createKitIcon(Heart);

const Share2Icon = createKitIcon(Share2);

const PhoneIcon = createKitIcon(Phone);

const MessageSquareIcon = createKitIcon(MessageSquare);

const TrendingDownIcon = createKitIcon(TrendingDown);

const ChevronLeftIcon = createKitIcon(ChevronLeft);

const ChevronRightIcon = createKitIcon(ChevronRight);

const RulerIcon = createKitIcon(Ruler);

const BedIcon = createKitIcon(Bed);

const Building2Icon = createKitIcon(Building2);

const HourglassIcon = createKitIcon(Hourglass);

const FlameIcon = createKitIcon(Flame);

const WalletIcon = createKitIcon(Wallet);

const WavesIcon = createKitIcon(Waves);

const ShipIcon = createKitIcon(Ship);

const ShoppingCartIcon = createKitIcon(ShoppingCart);

const NAV = [
    "sale",
    "rent",
    "projects",
    "agents"
] as const;

const FACTS = [
    {
        icon: RulerIcon,
        key: "area"
    },
    {
        icon: BedIcon,
        key: "rooms"
    },
    {
        icon: Building2Icon,
        key: "floor"
    },
    {
        icon: HourglassIcon,
        key: "age"
    },
    {
        icon: FlameIcon,
        key: "heating"
    },
    {
        icon: WalletIcon,
        key: "dues"
    }
] as const;

const FEATURES = [
    "sea",
    "south",
    "balconies",
    "bath",
    "dressing",
    "parking",
    "security",
    "elevator",
    "mortgage"
] as const;

const NEARBY = [
    {
        icon: WavesIcon,
        key: "shore",
        minutes: 4
    },
    {
        icon: ShipIcon,
        key: "ferry",
        minutes: 12
    },
    {
        icon: ShoppingCartIcon,
        key: "market",
        minutes: 6
    }
] as const;

const HISTORY = [
    {
        date: new Date(2026, 5, 1),
        price: 20.5
    },
    {
        date: new Date(2026, 6, 1),
        price: 20.5
    },
    {
        date: new Date(2026, 7, 1),
        price: 19.9
    },
    {
        date: new Date(2026, 8, 1),
        price: 19.5
    },
    {
        date: new Date(2026, 9, 1),
        price: 19
    },
    {
        date: new Date(2026, 10, 1),
        price: 18.75
    }
];

const TERMS = [
    60,
    84,
    120,
    180,
    240
];

const SLOTS = [
    "10:00",
    "11:30",
    "13:00",
    "14:30",
    "16:00",
    "17:30"
];

const BUSY_SLOTS = [
    "11:30",
    "17:30"
];

const FIRST_WEEK = new Date(2026, 10, 2);

const BUSY_DAYS = [
    2,
    6
];

const PRICE = 18750000;

const AREA = 148;

const DOWN_MIN = 10;

const DOWN_MAX = 80;

const RATE_STEP = 0.05;

const RATE_MIN = 0.1;

const RATE_MAX = 5;

const PLAN_RATIO = 820 / 470;

const SCENE_RATIO = 3 / 2;

const SCENES = [
    "sea",
    "sunset",
    "kitchen"
] as const;

const PLAN_LABELS = [
    {
        key: "salon",
        area: "34",
        x: 240,
        y: 236
    },
    {
        key: "kitchen",
        area: "12",
        x: 162,
        y: 116
    },
    {
        key: "hall",
        area: "6",
        x: 310,
        y: 124
    },
    {
        key: "kids",
        area: "14",
        x: 520,
        y: 108
    },
    {
        key: "bath",
        area: "6",
        x: 610,
        y: 104
    },
    {
        key: "wc",
        x: 710,
        y: 132
    },
    {
        key: "corridor",
        x: 570,
        y: 196
    },
    {
        key: "master",
        area: "18",
        x: 480,
        y: 252
    },
    {
        key: "bedroom",
        area: "13",
        x: 670,
        y: 252
    },
    {
        key: "balconyBig",
        x: 220,
        y: 416
    },
    {
        key: "balconySmall",
        x: 480,
        y: 416
    }
] as const;

const PLAN_FILLS = [
    "60 160 320 230",
    "380 220 200 170",
    "580 220 180 170",
    "380 30 180 130"
];

const PLAN_WET_FILLS = [
    "560 30 100 130",
    "660 30 100 130"
];

const PLAN_PLAIN_FILLS = [
    "60 30 180 130",
    "240 30 140 130",
    "380 160 380 60"
];

const PLAN_FURNITURE = [
    "M84 326 H234 V366 H84 Z",
    "M112 268 H206 V304 H112 Z",
    "M72 176 H182 V194 H72 Z",
    "M70 40 H230 V68 H98 V150 H70 Z",
    "M404 44 H496 V148 H404 Z",
    "M570 40 H650 V74 H570 Z",
    "M696 42 H726 V64 H696 Z",
    "M428 268 H532 V380 H428 Z",
    "M392 272 H418 V298 H392 Z",
    "M542 272 H568 V298 H542 Z",
    "M636 268 H720 V380 H636 Z",
    "M592 236 H622 V380 H592 Z"
];

const PLAN_ROUND_FURNITURE = [
    [
        300,
        250,
        30
    ],
    [
        300,
        206,
        9
    ],
    [
        300,
        294,
        9
    ],
    [
        256,
        250,
        9
    ],
    [
        344,
        250,
        9
    ],
    [
        130,
        54,
        7
    ],
    [
        160,
        54,
        7
    ],
    [
        610,
        124,
        10
    ]
];

const PLAN_WALLS = [
    "M60 160 H290 M340 160 H410 M450 160 H575 M615 160 H690 M725 160 H760",
    "M240 30 V80 M240 120 V160",
    "M380 30 V160 M380 220 V390",
    "M560 30 V160 M660 30 V160",
    "M380 220 H440 M480 220 H650 M690 220 H760",
    "M580 220 V390"
];

const PLAN_OUTER = "M60 30 H270 M320 30 H760 V390 H60 Z";

const PLAN_DOORS = [
    "M290 160 V210 A50 50 0 0 0 340 160",
    "M240 120 H200 A40 40 0 0 1 240 80",
    "M410 160 V120 A40 40 0 0 1 450 160",
    "M575 160 V120 A40 40 0 0 1 615 160",
    "M725 160 V125 A35 35 0 0 0 690 160",
    "M440 220 V260 A40 40 0 0 0 480 220",
    "M690 220 V260 A40 40 0 0 1 650 220",
    "M270 30 V80 A50 50 0 0 0 320 30"
];

const PLAN_WINDOWS = [
    "100 27 90 6",
    "410 27 100 6",
    "586 27 48 6",
    "757 60 6 70",
    "757 260 6 90",
    "57 210 6 100"
];

const PLAN_WAVES = [
    "M84 448 q10 -8 20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0",
    "M404 448 q10 -8 20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0"
];

const SEA_WAVES = [
    "M20 130 q10 -6 20 0 t20 0 t20 0",
    "M180 160 q10 -6 20 0 t20 0 t20 0",
    "M70 180 q10 -6 20 0 t20 0 t20 0"
];

const MAP_STREETS = [
    "M0 60 L330 100",
    "M60 0 L100 230",
    "M200 0 L240 230",
    "M0 150 L330 170"
];

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        width: "100%"
    },
    spread: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    footer: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        width: "100%"
    },
    overlay: {
        pointerEvents: "none",
        ...StyleSheet.absoluteFill
    },
    hero: {
        alignItems: "flex-end",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    labelWrap: {
        pointerEvents: "none",
        position: "absolute"
    },
    page: {
        alignItems: "center",
        minHeight: "100%",
        flexGrow: 1
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    centered: {
        alignItems: "center"
    },
    caption: {
        position: "absolute"
    },
    scene: {
        overflow: "hidden",
        flex: 1
    },
    center: {
        textAlign: "center"
    },
    shrink: {
        flexShrink: 1,
        minWidth: 0
    },
    ratio: {
        width: "100%"
    },
    full: {
        width: "100%"
    },
    shell: {
        width: "100%"
    },
    grow: {
        flex: 1,
        flexGrow: 1
    },
    heart: {
        flexGrow: 0
    },
    cell: {
        flexGrow: 1
    },
    day: {
        flex: 1
    }
});

type PlanLabelProps = {
    value?: string;
    title: string;
    x: number;
    y: number;
};

const PlanLabel = ({
    title,
    value,
    x,
    y
}: PlanLabelProps) => <View
    style={[
        stylesheet.labelWrap,
        stylesheet.centered,
        {
            left: `${(x - 70) / 8.2}%`,
            top: `${(y - 13) / 4.7}%`,
            width: `${140 / 8.2}%`
        }
    ]}
>
    <Text
        style={stylesheet.center}
        variant="labelSmallSize"
        numberOfLines={1}
        color="high"
    >
        {title}
    </Text>
    {
        value ?
            <Text
                variant="bodySmallSize"
                numberOfLines={1}
                color="mid"
            >
                {value}
            </Text>
        :
            null
    }
</View>;

const parseRect = (value: string) => value.split(" ").map(Number) as [number, number, number, number];

const FloorPlan = ({
    labels
}: {
    labels: Record<string, {
        value?: string;
        title: string;
    }>;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        categoricalArea,
        categorical
    } = colors.chart;

    const {
        container,
        icon,
        text
    } = colors.content;

    return <View
        style={[
            stylesheet.ratio,
            {
                aspectRatio: PLAN_RATIO
            }
        ]}
    >
        <Svg
            viewBox="0 0 820 470"
            height="100%"
            width="100%"
        >
            {
                PLAN_FILLS.map((value) => {
                    const [
                        x,
                        y,
                        width,
                        height
                    ] = parseRect(value);

                    return <Rect
                        fill={container.selected}
                        height={height}
                        width={width}
                        key={value}
                        x={x}
                        y={y}
                    />;
                })
            }
            {
                PLAN_PLAIN_FILLS.map((value) => {
                    const [
                        x,
                        y,
                        width,
                        height
                    ] = parseRect(value);

                    return <Rect
                        fill={container.default}
                        height={height}
                        width={width}
                        key={value}
                        x={x}
                        y={y}
                    />;
                })
            }
            {
                PLAN_WET_FILLS.map((value) => {
                    const [
                        x,
                        y,
                        width,
                        height
                    ] = parseRect(value);

                    return <Rect
                        fill={categoricalArea["3"]}
                        height={height}
                        width={width}
                        key={value}
                        x={x}
                        y={y}
                    />;
                })
            }
            <Rect
                fill={categoricalArea["1"]}
                stroke={text.mid}
                strokeWidth={2}
                height={44}
                width={280}
                y={390}
                x={80}
            />
            <Rect
                fill={categoricalArea["1"]}
                stroke={text.mid}
                strokeWidth={2}
                height={44}
                width={160}
                x={400}
                y={390}
            />
            <G
                strokeLinejoin="round"
                stroke={icon.low}
                strokeWidth={1.5}
                fill="none"
            >
                {
                    PLAN_FURNITURE.map((value) => <Path
                        key={value}
                        d={value}
                    />)
                }
                {
                    PLAN_ROUND_FURNITURE.map(([
                        cx,
                        cy,
                        r
                    ]) => <Circle
                        key={`${cx}-${cy}`}
                        cx={cx}
                        cy={cy}
                        r={r}
                    />)
                }
                <Ellipse
                    cx={711}
                    ry={14}
                    rx={11}
                    cy={96}
                />
            </G>
            <G
                strokeLinecap="square"
                stroke={text.mid}
                strokeWidth={2}
                fill="none"
            >
                {
                    PLAN_WALLS.map((value) => <Path
                        key={value}
                        d={value}
                    />)
                }
            </G>
            <Path
                strokeLinejoin="miter"
                stroke={text.mid}
                strokeWidth={4}
                d={PLAN_OUTER}
                fill="none"
            />
            <Path
                d="M96 390 H344 M416 390 H544"
                stroke={container.default}
                strokeWidth={6}
                fill="none"
            />
            <Path
                d="M96 388 H344 M96 392 H344 M416 388 H544 M416 392 H544"
                stroke={categorical["3"]}
                strokeWidth={1.5}
                fill="none"
            />
            {
                PLAN_WINDOWS.map((value) => {
                    const [
                        x,
                        y,
                        width,
                        height
                    ] = parseRect(value);

                    return <Rect
                        stroke={categorical["3"]}
                        fill={container.default}
                        strokeWidth={1.5}
                        height={height}
                        width={width}
                        key={value}
                        x={x}
                        y={y}
                    />;
                })
            }
            <G
                stroke={icon.low}
                strokeWidth={1}
                fill="none"
            >
                {
                    PLAN_DOORS.map((value) => <Path
                        key={value}
                        d={value}
                    />)
                }
            </G>
            <Path
                d="M295 12 V24 M290 19 l5 5 5-5"
                strokeLinejoin="round"
                strokeLinecap="round"
                stroke={text.high}
                strokeWidth={2}
                fill="none"
            />
            <G
                stroke={categorical["3"]}
                strokeLinecap="round"
                strokeOpacity={0.7}
                strokeWidth={2}
                fill="none"
            >
                {
                    PLAN_WAVES.map((value) => <Path
                        key={value}
                        d={value}
                    />)
                }
            </G>
            <G
                transform="translate(790 70)"
            >
                <Circle
                    stroke={colors.content.border.default}
                    fill="none"
                    r={14}
                />
                <Path
                    d="M0 -10 L4 4 L0 1 L-4 4 Z"
                    fill={text.high}
                />
            </G>
            <G
                transform="translate(640 448)"
                stroke={text.mid}
                strokeWidth={2}
                fill="none"
            >
                <Path
                    d="M0 0 H120"
                />
                <Path
                    d="M0 -4 V4 M60 -4 V4 M120 -4 V4"
                />
            </G>
        </Svg>
        <View
            style={stylesheet.overlay}
        >
            {
                PLAN_LABELS.map(({
                    key,
                    x,
                    y,
                    ...rest
                }) => <PlanLabel
                    value={labels[key]?.value ?? ("area" in rest ? `${rest.area} m²` : undefined)}
                    title={labels[key]?.title ?? ""}
                    key={key}
                    x={x}
                    y={y}
                />)
            }
        </View>
    </View>;
};

const SceneSea = () => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        categoricalArea,
        categorical
    } = colors.chart;

    const {
        container
    } = colors.content;

    return <Svg
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 300 200"
        height="100%"
        width="100%"
    >
        <Rect
            fill={categoricalArea["3"]}
            height={100}
            width={300}
            y={0}
            x={0}
        />
        <Circle
            fill={categorical["4"]}
            cx={215}
            cy={55}
            r={22}
        />
        <Path
            d="M0 100 L60 78 L120 100 Z M150 100 L210 82 L300 100 Z"
            fill={categoricalArea["7"]}
        />
        <Rect
            fill={categorical["3"]}
            fillOpacity={0.65}
            height={100}
            width={300}
            y={100}
            x={0}
        />
        {
            SEA_WAVES.map((value) => <Path
                stroke={container.default}
                strokeLinecap="round"
                strokeWidth={2}
                fill="none"
                key={value}
                d={value}
            />)
        }
        <Path
            d="M232 138 L252 138 L238 112 Z"
            fill={container.default}
        />
        <Path
            d="M226 140 L256 140 L250 148 L232 148 Z"
            fill={categorical["2"]}
        />
        <Rect
            fill={container.default}
            width={300}
            height={8}
            y={96}
            x={0}
        />
        <Rect
            fill={container.default}
            height={200}
            width={8}
            x={146}
            y={0}
        />
        <Path
            d="M20 200 Q40 160 60 200 Z M36 200 Q56 150 80 200 Z"
            fill={categorical["6"]}
        />
    </Svg>;
};

const SceneSunset = () => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        categoricalArea,
        categorical
    } = colors.chart;

    const {
        text
    } = colors.content;

    const posts = [
        20,
        50,
        80,
        110,
        140,
        170,
        200,
        230,
        260,
        290
    ];

    return <Svg
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 300 200"
        height="100%"
        width="100%"
    >
        <Rect
            fill={categoricalArea["2"]}
            height={200}
            width={300}
            y={0}
            x={0}
        />
        <Circle
            fill={categorical["2"]}
            cx={140}
            cy={112}
            r={48}
        />
        <Rect
            fill={categoricalArea["3"]}
            height={60}
            width={300}
            y={118}
            x={0}
        />
        <Rect
            fill={categorical["3"]}
            fillOpacity={0.45}
            height={60}
            width={300}
            y={118}
            x={0}
        />
        <Path
            d="M236 118 L241 98 L246 118 Z"
            fill={text.high}
        />
        <Rect
            fill={text.mid}
            width={300}
            height={7}
            y={150}
            x={0}
        />
        {
            posts.map((value) => <Line
                stroke={text.mid}
                strokeWidth={4}
                key={value}
                x1={value}
                x2={value}
                y1={156}
                y2={200}
            />)
        }
    </Svg>;
};

const SceneKitchen = () => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        categoricalArea,
        categorical
    } = colors.chart;

    const {
        text
    } = colors.content;

    const jars = [
        {
            color: categorical["2"],
            height: 26,
            x: 70
        },
        {
            color: categorical["4"],
            height: 34,
            x: 100
        },
        {
            color: categorical["3"],
            height: 22,
            x: 130
        },
        {
            color: categorical["8"],
            height: 30,
            x: 156
        },
        {
            color: categorical["6"],
            height: 20,
            x: 186
        }
    ];

    return <Svg
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 300 200"
        height="100%"
        width="100%"
    >
        <Rect
            fill={categoricalArea["7"]}
            height={200}
            width={300}
            y={0}
            x={0}
        />
        <Rect
            fill={categorical["7"]}
            fillOpacity={0.8}
            height={50}
            width={300}
            y={150}
            x={0}
        />
        <Rect
            fill={text.high}
            width={170}
            height={4}
            y={84}
            x={60}
        />
        {
            jars.map(({
                height,
                color,
                x
            }) => <Rect
                y={84 - height}
                height={height}
                fill={color}
                width={20}
                key={x}
                x={x}
            />)
        }
        <Line
            stroke={text.high}
            strokeWidth={1.5}
            x1={215}
            x2={215}
            y2={30}
            y1={0}
        />
        <Path
            d="M200 44 A15 15 0 0 1 230 44 Z"
            fill={categorical["4"]}
        />
        <Circle
            fill={categorical["2"]}
            cx={190}
            cy={136}
            r={20}
        />
        <Rect
            fill={categorical["2"]}
            height={8}
            width={20}
            x={180}
            y={112}
        />
    </Svg>;
};

const MapDrawing = () => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        categoricalArea,
        categorical
    } = colors.chart;

    const {
        container
    } = colors.content;

    return <Svg
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 330 230"
        height="100%"
        width="100%"
    >
        <Rect
            fill={categoricalArea["6"]}
            height={230}
            width={330}
            y={0}
            x={0}
        />
        <Rect
            fill={categoricalArea["3"]}
            height={50}
            width={330}
            y={180}
            x={0}
        />
        {
            MAP_STREETS.map((value) => <Path
                stroke={container.default}
                strokeLinecap="round"
                strokeWidth={10}
                fill="none"
                key={value}
                d={value}
            />)
        }
        <Circle
            fill={categorical["2"]}
            cx={165}
            cy={110}
            r={14}
        />
        <Circle
            fill={container.default}
            cx={165}
            cy={110}
            r={5}
        />
    </Svg>;
};

const SCENE_ARTS = {
    kitchen: SceneKitchen,
    sunset: SceneSunset,
    sea: SceneSea
};

const handlePress = () => undefined;

const getOptionTitle = (option: {
    name: string;
}) => option.name;

const getOptionKey = (option: {
    id: string;
}) => option.id;

const RealEstate01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        nav,
        setNav
    ] = useState<string>("sale");

    const [
        view,
        setView
    ] = useState("plan");

    const [
        photo,
        setPhoto
    ] = useState<string>("sea");

    const [
        isSaved,
        setIsSaved
    ] = useState(false);

    const [
        down,
        setDown
    ] = useState<SliderValue>(30);

    const [
        term,
        setTerm
    ] = useState(120);

    const [
        rateText,
        setRateText
    ] = useState(() => new Intl.NumberFormat(activeLocale).format(0.99));

    const [
        weekOffset,
        setWeekOffset
    ] = useState(0);

    const [
        dayIndex,
        setDayIndex
    ] = useState(3);

    const [
        slot,
        setSlot
    ] = useState<string | undefined>("13:00");

    const [
        isRequested,
        setIsRequested
    ] = useState(false);

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const currency = new Intl.NumberFormat(activeLocale, {
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const compactCurrency = new Intl.NumberFormat(activeLocale, {
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 2,
        notation: "compact",
        style: "currency",
        currency: "TRY"
    });

    const monthFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short"
    });

    const percent = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1,
        style: "percent"
    });

    const weekdayFormat = new Intl.DateTimeFormat(activeLocale, {
        weekday: "short"
    });

    const dayNumberFormat = new Intl.DateTimeFormat(activeLocale, {
        day: "numeric"
    });

    const rangeEndFormat = new Intl.DateTimeFormat(activeLocale, {
        year: "numeric",
        day: "numeric",
        month: "long"
    });

    const dayLongFormat = new Intl.DateTimeFormat(activeLocale, {
        weekday: "long",
        day: "numeric",
        month: "long"
    });

    const downPercent = typeof down === "number" ? down : DOWN_MIN;

    const rate = Number(rateText.replace(",", "."));

    const monthlyRate = Number.isFinite(rate) ? rate / 100 : 0;

    const loan = PRICE * (1 - downPercent / 100);

    const payment = monthlyRate > 0 ?
        loan * monthlyRate / (1 - Math.pow(1 + monthlyRate, -term)) :
        loan / term;

    const total = payment * term;

    const weekStart = new Date(FIRST_WEEK.getTime());

    weekStart.setDate(FIRST_WEEK.getDate() + weekOffset * 7);

    const weekDays = Array.from({
        length: 7
    }, (_, index) => {
        const day = new Date(weekStart.getTime());

        day.setDate(weekStart.getDate() + index);

        return day;
    });

    const weekEnd = weekDays[6] ?? weekStart;

    const selectedDay = weekDays[dayIndex] ?? weekStart;

    const terms = TERMS.map((months) => ({
        name: t("blocks.realEstate01.termMonths", [months]),
        id: String(months)
    }));

    const planLabels = Object.fromEntries(PLAN_LABELS.map(({
        key
    }) => [
        key,
        {
            title: t(`blocks.realEstate01.plan.${key}`)
        }
    ]));

    const handleNavPress = (key: string) => () => setNav(key);

    const handleViewChange = (value: string | Array<string>) => setView(typeof value === "string" && value !== "" ? value : view);

    const handleSavePress = () => setIsSaved(!isSaved);

    const handleDownChange = (value: SliderValue) => setDown(value);

    const handleTermChange = (items: Array<{
        __key: string;
    }>) => {
        const [
            first
        ] = items;

        if(first) {
            setTerm(Number(first.__key));
        }
    };

    const handleRateChange = (value: string) => setRateText(value);

    const handleWeekChange = (step: number) => () => {
        setWeekOffset(weekOffset + step);

        setSlot(undefined);

        setIsRequested(false);
    };

    const handleDayPress = (index: number) => () => {
        setDayIndex(index);

        setSlot(undefined);

        setIsRequested(false);
    };

    const handleSlotPress = (value: string) => () => {
        setSlot(value);

        setIsRequested(false);
    };

    const handleRequestPress = () => setIsRequested(true);

    const handlePhotoPress = (key: string) => () => {
        setPhoto(key);

        setView("photos");
    };

    const renderNav = (key: string) => <Button
        title={t(`blocks.realEstate01.nav.${key}`)}
        variant={nav === key ? "tonal" : "ghost"}
        onPress={handleNavPress(key)}
        type="neutral"
        size="small"
        key={key}
    />;

    const renderScene = (key: keyof typeof SCENE_ARTS) => {
        const Art = SCENE_ARTS[key];

        return <Art
            key={key}
        />;
    };

    const renderTile = (key: keyof typeof SCENE_ARTS) => <View
        style={[
            stylesheet.scene,
            {
                minWidth: isCompact ? "100%" : shapeConfigs.fieldMinWidth * 0.75,
                borderColor: colors.content.border.default,
                borderRadius: radiuses.surface,
                borderWidth: borders.separator,
                aspectRatio: SCENE_RATIO
            }
        ]}
        key={key}
    >
        {renderScene(key)}
        <View
            style={[
                stylesheet.caption,
                {
                    bottom: spaces.spacingSm,
                    left: spaces.spacingSm
                }
            ]}
        >
            <Chip
                title={t(`blocks.realEstate01.scene.${key}`)}
                onPress={handlePhotoPress(key)}
                variant="outline"
                type="neutral"
                size="small"
            />
        </View>
    </View>;

    const renderFact = ({
        icon,
        key
    }: typeof FACTS[number]) => <View
        style={[
            stylesheet.cell,
            {
                flexBasis: shapeConfigs.fieldMinWidth + spaces.spacingXl
            }
        ]}
        key={key}
    >
        <Item
            size="small"
        >
            <Item.Media
                variant="icon"
                icon={icon}
            />
            <Item.Content>
                <Item.Description>
                    {t(`blocks.realEstate01.fact.${key}`)}
                </Item.Description>
                <Item.Title>
                    {t(`blocks.realEstate01.fact.${key}Value`)}
                </Item.Title>
                <Item.Description>
                    {t(`blocks.realEstate01.fact.${key}Note`)}
                </Item.Description>
            </Item.Content>
        </Item>
    </View>;

    const renderFeature = (key: string) => <Badge
        title={t(`blocks.realEstate01.feature.${key}`)}
        variant="outline"
        type="neutral"
        size="large"
        key={key}
    />;

    const renderNearby = ({
        minutes,
        icon,
        key
    }: typeof NEARBY[number]) => <Item
        size="small"
        key={key}
    >
        <Item.Media
            variant="icon"
            icon={icon}
        />
        <Item.Content>
            <Item.Title>
                {t(`blocks.realEstate01.nearby.${key}`)}
            </Item.Title>
        </Item.Content>
        <Item.Actions>
            <Text
                variant="bodyMediumSize"
                color="mid"
            >
                {t("blocks.realEstate01.walk", [
                    minutes
                ])}
            </Text>
        </Item.Actions>
    </Item>;

    const renderDay = (day: Date, index: number) => <Button
        variant={index === dayIndex ? "filled" : "ghost"}
        type={index === dayIndex ? "primary" : "neutral"}
        isDisabled={BUSY_DAYS.includes(index)}
        title={dayNumberFormat.format(day)}
        accessibilityState={{
            selected: index === dayIndex
        }}
        onPress={handleDayPress(index)}
        style={stylesheet.day}
        key={index}
    />;

    const renderWeekday = (day: Date) => <Text
        variant="labelSmallSize"
        style={[
            stylesheet.day,
            stylesheet.center
        ]}
        key={day.getTime()}
        color="mid"
    >
        {weekdayFormat.format(day)}
    </Text>;

    const renderSlot = (value: string) => <Chip
        isDisabled={BUSY_SLOTS.includes(value)}
        onPress={handleSlotPress(value)}
        isSelected={slot === value}
        variant="outline"
        type="neutral"
        title={value}
        key={value}
    />;

    const historyRows = HISTORY.map(({
        price,
        date
    }) => ({
        date: date.toISOString(),
        price: price * 1000000
    }));

    const stars = [
        1,
        2,
        3,
        4,
        5
    ];

    const planView = <ScrollView
        contentContainerStyle={[
            stylesheet.grow,
            {
                minWidth: shapeConfigs.sheetMaxWidth
            }
        ]}
        horizontal={true}
    >
        <View
            style={stylesheet.ratio}
        >
            <FloorPlan
                labels={planLabels}
            />
        </View>
    </ScrollView>;

    const photoKey = SCENES.includes(photo as typeof SCENES[number]) ? photo : "sea";

    const photosView = <View
        style={[
            stylesheet.ratio,
            stylesheet.scene,
            {
                borderRadius: radiuses.surface,
                aspectRatio: PLAN_RATIO
            }
        ]}
    >
        {renderScene(photoKey as keyof typeof SCENE_ARTS)}
    </View>;

    const mapView = <View
        style={[
            stylesheet.ratio,
            stylesheet.scene,
            {
                borderRadius: radiuses.surface,
                aspectRatio: PLAN_RATIO
            }
        ]}
    >
        <MapDrawing/>
    </View>;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
    >
        <View
            style={[
                stylesheet.header,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    accessibilityRole="header"
                    variant="titleLargeSize"
                    color="high"
                >
                    {t("blocks.realEstate01.brand")}
                </Text>
                {
                    isCompact ?
                        null
                    :
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            {NAV.map(renderNav)}
                        </View>
                }
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Button
                    accessibilityLabel={t("blocks.realEstate01.favorites")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={HeartIcon}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
                {
                    isCompact ?
                        null
                    :
                        <Button
                            title={t("blocks.realEstate01.signIn")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                }
                <Button
                    title={t("blocks.realEstate01.postListing")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="tonal"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
        <Separator
            length="100%"
        />
        <View
            style={[
                stylesheet.shell,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <View
                style={{
                    gap: spaces.spacingMd
                }}
            >
                <Breadcrumb
                    accessibilityLabel={t("blocks.realEstate01.breadcrumb")}
                    compactBehaviour="collapse"
                >
                    <Breadcrumb.List>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.realEstate01.crumbSale")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.realEstate01.crumbCity")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.realEstate01.crumbDistrict")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Page
                                title={t("blocks.realEstate01.crumbArea")}
                            />
                        </Breadcrumb.Item>
                    </Breadcrumb.List>
                </Breadcrumb>
            </View>
            <View
                style={{
                    flexDirection: isWide ? "row" : "column",
                    alignItems: "flex-start",
                    gap: spaces.spacingXl
                }}
            >
                <View
                    style={[
                        stylesheet.shrink,
                        {
                            width: isWide ? undefined : "100%",
                            flex: isWide ? 1 : undefined,
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.hero,
                            {
                                justifyContent: "space-between",
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.shrink,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Badge
                                    title={t("blocks.realEstate01.badgeSale")}
                                    variant="outline"
                                    type="neutral"
                                />
                                <Badge
                                    title={t("blocks.realEstate01.badgeDrop")}
                                    icon={TrendingDownIcon}
                                    type="success"
                                />
                                <Badge
                                    title={t("blocks.realEstate01.badgeMortgage")}
                                    type="neutral"
                                />
                            </View>
                            <Text
                                variant="headlineMediumSize"
                                accessibilityRole="header"
                                color="high"
                            >
                                {t("blocks.realEstate01.title")}
                            </Text>
                            <View
                                style={[
                                    stylesheet.wrap,
                                    {
                                        columnGap: spaces.spacingMd,
                                        rowGap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <Icon
                                        icon={MapPin}
                                        size="small"
                                        color="mid"
                                    />
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.realEstate01.address")}
                                    </Text>
                                </View>
                                <Text
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.realEstate01.listingNo")}
                                </Text>
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Button
                                title={t("blocks.realEstate01.share")}
                                onPress={handlePress}
                                icon={Share2Icon}
                                variant="outline"
                                type="neutral"
                            />
                            <Button
                                title={t(isSaved ? "blocks.realEstate01.saved" : "blocks.realEstate01.save")}
                                variant={isSaved ? "tonal" : "outline"}
                                onPress={handleSavePress}
                                icon={HeartIcon}
                                type="neutral"
                            />
                        </View>
                    </View>
                    <Card
                        variant="outline"
                    >
                        <Card.Content>
                            <View
                                style={[
                                    stylesheet.spread,
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <ToggleGroup
                                    title={t("blocks.realEstate01.viewTitle")}
                                    onValueChange={handleViewChange}
                                    attachBehaviour="attached"
                                    isClearEnabled={false}
                                    variant="outline"
                                    value={view}
                                    size="small"
                                >
                                    <ToggleGroup.Item
                                        title={t("blocks.realEstate01.viewPlan")}
                                        value="plan"
                                    />
                                    <ToggleGroup.Item
                                        title={t("blocks.realEstate01.viewPhotos")}
                                        value="photos"
                                    />
                                    <ToggleGroup.Item
                                        title={t("blocks.realEstate01.viewMap")}
                                        value="map"
                                    />
                                </ToggleGroup>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.realEstate01.planMeta")}
                                </Text>
                            </View>
                            {view === "plan" ? planView : view === "photos" ? photosView : mapView}
                        </Card.Content>
                    </Card>
                    <View
                        style={[
                            stylesheet.wrap,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {SCENES.map(renderTile)}
                    </View>
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.realEstate01.aboutTitle")}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={[
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingLg
                                    }
                                ]}
                            >
                                {FACTS.map(renderFact)}
                            </View>
                            <Separator/>
                            <View
                                style={{
                                    gap: spaces.spacingSm
                                }}
                            >
                                <Text
                                    variant="labelMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.realEstate01.featuresTitle")}
                                </Text>
                                <View
                                    style={[
                                        stylesheet.wrap,
                                        {
                                            gap: spaces.spacingSm
                                        }
                                    ]}
                                >
                                    {FEATURES.map(renderFeature)}
                                </View>
                            </View>
                        </Card.Content>
                    </Card>
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.realEstate01.descriptionTitle")}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Text
                                style={{
                                    maxWidth: shapeConfigs.readingMaxWidth + shapeConfigs.fieldMinWidth
                                }}
                                variant="readingMediumSize"
                                color="mid"
                            >
                                {t("blocks.realEstate01.description")}
                            </Text>
                            <Text
                                style={{
                                    maxWidth: shapeConfigs.readingMaxWidth + shapeConfigs.fieldMinWidth
                                }}
                                variant="readingMediumSize"
                                color="mid"
                            >
                                {t("blocks.realEstate01.descriptionMore")}
                            </Text>
                        </Card.Content>
                    </Card>
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.realEstate01.nearbyTitle")}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            {NEARBY.map(renderNearby)}
                        </Card.Content>
                    </Card>
                </View>
                <View
                    style={[
                        stylesheet.shrink,
                        {
                            width: isWide ? shapeConfigs.cardWidth + spaces.spacingXl : "100%",
                            gap: spaces.spacingLg,
                            flexGrow: 0
                        }
                    ]}
                >
                    <Card
                        variant="outline"
                    >
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingXs
                                }}
                            >
                                <Text
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.realEstate01.priceLabel")}
                                </Text>
                                <Text
                                    variant="headlineLargeSize"
                                    color="high"
                                >
                                    {currency.format(PRICE)}
                                </Text>
                                <Text
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.realEstate01.pricePerM2", [
                                        currency.format(Math.round(PRICE / AREA)),
                                        AREA
                                    ])}
                                </Text>
                            </View>
                            <View
                                style={[
                                    stylesheet.spread,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Text
                                    accessibilityRole="header"
                                    variant="titleSmallSize"
                                    color="high"
                                >
                                    {t("blocks.realEstate01.historyTitle")}
                                </Text>
                                <Badge
                                    title={t("blocks.realEstate01.historyDrop", [
                                        percent.format(0.085)
                                    ])}
                                    icon={TrendingDownIcon}
                                    type="success"
                                />
                            </View>
                            <Chart
                                config={{
                                    price: {
                                        title: t("blocks.realEstate01.priceLabel"),
                                        colorIndex: 1
                                    }
                                }}
                                title={t("blocks.realEstate01.historyTitle")}
                                spreadBehaviour="stretch"
                                isAnimatedOnMount={true}
                            >
                                <LineChart
                                    categoryFormatter={(value) => monthFormat.format(new Date(String(value)))}
                                    valueFormatter={(value) => compactCurrency.format(value)}
                                    categoryTitle={t("blocks.realEstate01.month")}
                                    data={historyRows}
                                    categoryKey="date"
                                    plotSize="small"
                                    curve="natural"
                                    seriesKeys={[
                                        "price"
                                    ]}
                                />
                            </Chart>
                            <Separator/>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <View
                                    style={{
                                        gap: spaces.spacingXxs
                                    }}
                                >
                                    <Text
                                        accessibilityRole="header"
                                        variant="titleMediumSize"
                                        color="high"
                                    >
                                        {t("blocks.realEstate01.calcTitle")}
                                    </Text>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.realEstate01.calcLead")}
                                    </Text>
                                </View>
                                <Slider
                                    formatValueText={(value) => `${percent.format(value / 100)} · ${currency.format(PRICE * value / 100)}`}
                                    title={t("blocks.realEstate01.down")}
                                    onValueChange={handleDownChange}
                                    isShowMinMaxLabels={true}
                                    value={downPercent}
                                    min={DOWN_MIN}
                                    max={DOWN_MAX}
                                    step={5}
                                />
                                <View
                                    style={[
                                        stylesheet.wrap,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <View
                                        style={[
                                            stylesheet.cell,
                                            {
                                                flexBasis: shapeConfigs.fieldMinWidth
                                            }
                                        ]}
                                    >
                                        <SelectBox
                                            title={t("blocks.realEstate01.term")}
                                            titleExtractor={getOptionTitle}
                                            defaultValue={String(term)}
                                            keyExtractor={getOptionKey}
                                            onChange={handleTermChange}
                                            spreadBehaviour="stretch"
                                            data={terms}
                                        />
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.cell,
                                            {
                                                flexBasis: shapeConfigs.fieldMinWidth
                                            }
                                        ]}
                                    >
                                        <NumericInput
                                            title={t("blocks.realEstate01.rate")}
                                            onChangeText={handleRateChange}
                                            spreadBehaviour="stretch"
                                            isShowStepper={true}
                                            step={RATE_STEP}
                                            value={rateText}
                                            isDecimal={true}
                                            min={RATE_MIN}
                                            max={RATE_MAX}
                                            prefix="%"
                                        />
                                    </View>
                                </View>
                                <Card
                                    variant="filled"
                                >
                                    <Card.Content>
                                        <View>
                                            <Text
                                                variant="bodySmallSize"
                                                color="mid"
                                            >
                                                {t("blocks.realEstate01.monthly")}
                                            </Text>
                                            <Text
                                                variant="headlineMediumSize"
                                                color="high"
                                            >
                                                {currency.format(payment)}
                                            </Text>
                                        </View>
                                        <Separator/>
                                        {
                                            [
                                                [
                                                    "loan",
                                                    loan
                                                ],
                                                [
                                                    "total",
                                                    total
                                                ],
                                                [
                                                    "interest",
                                                    total - loan
                                                ]
                                            ].map(([
                                                key,
                                                value
                                            ]) => <View
                                                style={[
                                                    stylesheet.spread,
                                                    {
                                                        gap: spaces.spacingSm
                                                    }
                                                ]}
                                                key={String(key)}
                                            >
                                                <Text
                                                    variant="bodyMediumSize"
                                                    color="mid"
                                                >
                                                    {t(`blocks.realEstate01.${key}`)}
                                                </Text>
                                                <Text
                                                    variant="labelMediumSize"
                                                    color="high"
                                                >
                                                    {currency.format(Number(value))}
                                                </Text>
                                            </View>)
                                        }
                                    </Card.Content>
                                </Card>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.realEstate01.calcNote")}
                                </Text>
                            </View>
                        </Card.Content>
                    </Card>
                    <Card
                        variant="outline"
                    >
                        <Card.Content>
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        alignItems: "flex-start",
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Avatar
                                    title={t("blocks.realEstate01.agent")}
                                    size="large"
                                />
                                <View
                                    style={[
                                        stylesheet.shrink,
                                        {
                                            gap: spaces.spacingXs,
                                            flex: 1
                                        }
                                    ]}
                                >
                                    <View
                                        style={[
                                            stylesheet.row,
                                            {
                                                gap: spaces.spacingXs
                                            }
                                        ]}
                                    >
                                        <Text
                                            variant="titleMediumSize"
                                            numberOfLines={1}
                                            color="high"
                                        >
                                            {t("blocks.realEstate01.agent")}
                                        </Text>
                                        <Icon
                                            customColor={colors.content.text.info}
                                            icon={BadgeCheck}
                                            size="small"
                                        />
                                    </View>
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.realEstate01.agentRole")}
                                    </Text>
                                    <View
                                        style={[
                                            stylesheet.wrap,
                                            stylesheet.centered,
                                            {
                                                columnGap: spaces.spacingXs,
                                                rowGap: spaces.spacingXxs,
                                                flexDirection: "row"
                                            }
                                        ]}
                                    >
                                        <View
                                            accessibilityLabel={t("blocks.realEstate01.ratingLabel")}
                                            style={stylesheet.row}
                                        >
                                            {
                                                stars.map((star) => <Icon
                                                    customColor={colors.chart.categorical["4"]}
                                                    size="small"
                                                    icon={Star}
                                                    key={star}
                                                />)
                                            }
                                        </View>
                                        <Text
                                            variant="labelSmallSize"
                                            color="high"
                                        >
                                            {new Intl.NumberFormat(activeLocale, {
                                                minimumFractionDigits: 1
                                            }).format(4.9)}
                                        </Text>
                                        <Text
                                            variant="bodySmallSize"
                                            color="mid"
                                        >
                                            {t("blocks.realEstate01.reviews", [
                                                128
                                            ])}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.realEstate01.agentBio")}
                            </Text>
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Button
                                    title={t("blocks.realEstate01.call")}
                                    spreadBehaviour="stretch"
                                    style={stylesheet.grow}
                                    onPress={handlePress}
                                    variant="outline"
                                    icon={PhoneIcon}
                                    type="neutral"
                                />
                                <Button
                                    title={t("blocks.realEstate01.message")}
                                    spreadBehaviour="stretch"
                                    icon={MessageSquareIcon}
                                    style={stylesheet.grow}
                                    onPress={handlePress}
                                    variant="outline"
                                    type="neutral"
                                />
                            </View>
                        </Card.Content>
                    </Card>
                    <Card
                        variant="outline"
                    >
                        <Card.Content>
                            <View
                                style={[
                                    stylesheet.spread,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.shrink,
                                        {
                                            gap: spaces.spacingXxs,
                                            flex: 1
                                        }
                                    ]}
                                >
                                    <Text
                                        accessibilityRole="header"
                                        variant="titleMediumSize"
                                        color="high"
                                    >
                                        {t("blocks.realEstate01.visitTitle")}
                                    </Text>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.realEstate01.visitLead")}
                                    </Text>
                                </View>
                                <View
                                    style={stylesheet.row}
                                >
                                    <Button
                                        accessibilityLabel={t("blocks.realEstate01.prevWeek")}
                                        onPress={handleWeekChange(-1)}
                                        isDisabled={weekOffset === 0}
                                        icon={ChevronLeftIcon}
                                        variant="ghost"
                                        type="neutral"
                                        size="small"
                                    />
                                    <Button
                                        accessibilityLabel={t("blocks.realEstate01.nextWeek")}
                                        onPress={handleWeekChange(1)}
                                        icon={ChevronRightIcon}
                                        variant="ghost"
                                        type="neutral"
                                        size="small"
                                    />
                                </View>
                            </View>
                            <View
                                style={{
                                    gap: spaces.spacingXs
                                }}
                            >
                                <Text
                                    variant="labelMediumSize"
                                    color="high"
                                >
                                    {`${dayNumberFormat.format(weekStart)} – ${rangeEndFormat.format(weekEnd)}`}
                                </Text>
                                <View
                                    style={stylesheet.row}
                                >
                                    {weekDays.map(renderWeekday)}
                                </View>
                                <View
                                    style={stylesheet.row}
                                >
                                    {weekDays.map(renderDay)}
                                </View>
                            </View>
                            <View
                                style={{
                                    gap: spaces.spacingSm
                                }}
                            >
                                <Text
                                    variant="labelMediumSize"
                                    color="high"
                                >
                                    {dayLongFormat.format(selectedDay)}
                                </Text>
                                <View
                                    style={[
                                        stylesheet.wrap,
                                        {
                                            gap: spaces.spacingSm
                                        }
                                    ]}
                                >
                                    {SLOTS.map(renderSlot)}
                                </View>
                            </View>
                            {
                                isRequested ?
                                    <Alert
                                        description={t("blocks.realEstate01.requestedBody", [
                                            dayLongFormat.format(selectedDay),
                                            slot ?? ""
                                        ])}
                                        title={t("blocks.realEstate01.requestedTitle")}
                                        type="success"
                                    />
                                :
                                    null
                            }
                            <Button
                                title={t("blocks.realEstate01.request")}
                                onPress={handleRequestPress}
                                spreadBehaviour="stretch"
                                isDisabled={!slot}
                                variant="filled"
                                type="primary"
                            />
                            <Text
                                style={stylesheet.center}
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.realEstate01.requestNote")}
                            </Text>
                        </Card.Content>
                    </Card>
                </View>
            </View>
        </View>
        <Separator
            length="100%"
        />
        <View
            style={[
                stylesheet.wrap,
                stylesheet.footer,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingSm
                },
                isCompact ?
                    {
                        alignItems: "flex-start",
                        flexDirection: "column"
                    }
                :
                    null
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.realEstate01.copyright")}
            </Text>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    title={t("blocks.realEstate01.privacy")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.realEstate01.kvkk")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.realEstate01.rules")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
    </ScrollView>;
};
export default RealEstate01;
